import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useParams, useLocation, Navigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { PageSEO } from "@/components/PageSEO";
import { SiteStatsPanel } from "@/components/SiteStatsPanel";
import { ReportOrdersPanel } from "@/components/ReportOrdersPanel";
import { PrerenderStatusPanel } from "@/components/PrerenderStatusPanel";
import { QmdpTestPanel } from "@/components/QmdpTestPanel";
import { QmdpGestionePanel } from "@/components/QmdpGestionePanel";
import { SEO } from "@/lib/seo";
import {
  API, SERVICES, PROBLEMS, SOLUTIONS, METHOD_STEPS,
  TESTIMONIALS_DATA, LOCATIONS, SOCIAL_LINKS, WHATSAPP_NUMBER,
  CONTACT_EMAIL, TEAM_ITEMS
} from "@/lib/constants";
import {
  FileCheck, Compass, BarChart3, Phone, Mail, MapPin,
  Facebook, Instagram, Linkedin, Menu, X, Star, ArrowRight,
  CheckCircle, Users, Clock, Award, ChevronRight, MessageCircle,
  Building, Target, TrendingUp, Shield, Play, XCircle, Zap,
  Eye, Lock, AlertTriangle, Sparkles, ArrowDown, Edit, Trash2,
  Plus, Save, LogOut, Image, Download, ShoppingCart, FileSpreadsheet,
  HelpCircle, ChevronDown, CircleDollarSign, CalendarCheck, PieChart,
  FileText, Wallet, BadgePercent, Sparkle, RefreshCw, ShieldCheck, BookOpen
} from "lucide-react";

const AdminPage = () => {
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [token, setToken] = useState(localStorage.getItem('adminToken') || '');
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [showForm, setShowForm] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    excerpt: '',
    content: '',
    category: 'Controllo di Gestione',
    author: 'Silvia Casali',
    image_url: '',
    is_published: true
  });

  const [loginData, setLoginData] = useState({ username: '', password: '' });
  const [adminTab, setAdminTab] = useState('articles');
  const [paymentLinks, setPaymentLinks] = useState([]);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [paymentFormData, setPaymentFormData] = useState({
    title: '', description: '', amount: '', payment_type: 'one_time'
  });
  const [dashboardData, setDashboardData] = useState(null);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [coupons, setCoupons] = useState([]);
  const [showCouponForm, setShowCouponForm] = useState(false);
  const [couponFormData, setCouponFormData] = useState({ code: '', discount_percent: 100, max_uses: '', product_slug: '', expires_at: '' });
  const [consentStats, setConsentStats] = useState(null);
  const [consentLoading, setConsentLoading] = useState(false);
  const [checkupStats, setCheckupStats] = useState(null);
  const [checkupLoading, setCheckupLoading] = useState(false);
  const [abandonedLeads, setAbandonedLeads] = useState(null);
  const [abandonedLoading, setAbandonedLoading] = useState(false);
  const [forfettari, setForfettari] = useState(null);
  const [forfLoading, setForfLoading] = useState(false);
  const [featureFlags, setFeatureFlags] = useState(null);
  const [siteStats, setSiteStats] = useState(null);
  const [siteStatsLoading, setSiteStatsLoading] = useState(false);
  const [statsMonth, setStatsMonth] = useState(() => new Date().toISOString().slice(0, 7));

  const categories = [
    'Analisi',
    'Controllo di Gestione',
    'Fiscalita',
    'Normativa',
    'Pianificazione',
    'Strategia',
    'Impresa'
  ];

  // Check token on mount
  useEffect(() => {
    if (token) {
      verifyToken();
    }
  }, []);

  const verifyToken = async () => {
    try {
      await axios.get(`${API}/admin/verify`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIsLoggedIn(true);
      fetchArticles();
    } catch (e) {
      localStorage.removeItem('adminToken');
      setToken('');
      setIsLoggedIn(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, loginData);
      const newToken = res.data.token;
      setToken(newToken);
      localStorage.setItem('adminToken', newToken);
      setIsLoggedIn(true);
      toast.success('Login effettuato!');
      // Fetch articles with the new token directly (state update is async)
      fetchArticlesWithToken(newToken);
    } catch (e) {
      toast.error('Credenziali non valide');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/admin/logout`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (e) {}
    localStorage.removeItem('adminToken');
    setToken('');
    setIsLoggedIn(false);
    toast.success('Logout effettuato');
  };

  const fetchArticles = async () => {
    fetchArticlesWithToken(token);
  };

  const fetchArticlesWithToken = async (authToken) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/articles`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setArticles(res.data);
    } catch (e) {
      toast.error('Errore nel caricamento articoli');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editingArticle) {
        await axios.put(`${API}/admin/articles/${editingArticle.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Articolo aggiornato!');
      } else {
        await axios.post(`${API}/admin/articles`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Articolo creato!');
      }
      resetForm();
      fetchArticles();
    } catch (e) {
      toast.error('Errore nel salvataggio');
    } finally {
      setLoading(false);
    }
  };

  const [uploadingImage, setUploadingImage] = useState(false);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingImage(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/admin/blog-image`, fd, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFormData(prev => ({ ...prev, image_url: res.data.url }));
      toast.success('Immagine caricata');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nel caricamento immagine');
    } finally {
      setUploadingImage(false);
      e.target.value = '';
    }
  };

  const [importingHtml, setImportingHtml] = useState(false);

  const handleImportHtml = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImportingHtml(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/admin/blog-import`, fd, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message || 'Articolo pubblicato');
      fetchArticlesWithToken(token);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nell\'importazione');
    } finally {
      setImportingHtml(false);
      e.target.value = '';
    }
  };

  const handleSendNewsletter = async (article) => {
    if (!window.confirm(`Inviare la newsletter "${article.title}" a tutti gli iscritti?`)) return;
    try {
      const res = await axios.post(`${API}/admin/articles/${article.id}/send-newsletter`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nell\'invio della newsletter');
    }
  };

  const handleEdit = (article) => {
    setEditingArticle(article);
    setFormData({
      title: article.title,
      excerpt: article.excerpt,
      content: article.content,
      category: article.category,
      author: article.author,
      image_url: article.image_url || '',
      is_published: article.is_published
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo articolo?')) return;
    try {
      await axios.delete(`${API}/admin/articles/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Articolo eliminato');
      fetchArticles();
    } catch (e) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      excerpt: '',
      content: '',
      category: 'Controllo di Gestione',
      author: 'Silvia Casali',
      image_url: '',
      is_published: true
    });
    setEditingArticle(null);
    setShowForm(false);
  };

  // Payment Link functions
  const fetchPaymentLinks = async () => {
    try {
      const res = await axios.get(`${API}/admin/payment-links`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPaymentLinks(res.data);
    } catch (e) {
      toast.error('Errore caricamento link');
    }
  };

  const handleCreatePaymentLink = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/admin/payment-links`, {
        ...paymentFormData,
        amount: parseFloat(paymentFormData.amount),
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Link di pagamento creato!');
      setShowPaymentForm(false);
      setPaymentFormData({ title: '', description: '', amount: '', payment_type: 'one_time' });
      fetchPaymentLinks();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore nella creazione');
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePaymentLink = async (id) => {
    if (!window.confirm('Eliminare questo link di pagamento?')) return;
    try {
      await axios.delete(`${API}/admin/payment-links/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Link eliminato');
      fetchPaymentLinks();
    } catch (e) {
      toast.error('Errore');
    }
  };

  const handleTogglePaymentLink = async (id) => {
    try {
      await axios.put(`${API}/admin/payment-links/${id}/toggle`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchPaymentLinks();
    } catch (e) {
      toast.error('Errore');
    }
  };

  const handleRenameSlug = async (id, currentSlug) => {
    const nuovo = window.prompt('Nuovo slug (minuscole, cifre, trattini):', currentSlug || '');
    if (!nuovo || nuovo === currentSlug) return;
    try {
      await axios.patch(`${API}/admin/payment-links/${id}/slug`, { slug: nuovo }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Slug aggiornato');
      fetchPaymentLinks();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore rinomina slug');
    }
  };

  const getPaymentUrl = (slug) => {
    const base = window.location.origin;
    return `${base}/pay/${slug}`;
  };

  const copyLink = (slug) => {
    navigator.clipboard.writeText(getPaymentUrl(slug));
    toast.success('Link copiato!');
  };

  const [reconciling, setReconciling] = useState(false);

  const handleReconcilePurchases = async () => {
    setReconciling(true);
    try {
      const res = await axios.post(`${API}/admin/purchases/reconcile`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const d = res.data;
      toast.success(`Verificati ${d.checked} acquisti: ${d.confirmed_paid} pagati confermati (email inviate), ${d.expired} scaduti, ${d.still_pending} ancora in attesa`);
      fetchDashboard();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nella verifica pagamenti');
    } finally {
      setReconciling(false);
    }
  };

  const [newsletterData, setNewsletterData] = useState(null);

  const fetchNewsletterData = async () => {
    try {
      const [subsRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/newsletter-subscribers`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/newsletter-stats`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setNewsletterData({ ...subsRes.data, ...statsRes.data });
    } catch (e) {
      toast.error('Errore caricamento dati newsletter');
    }
  };

  const fetchDashboard = async () => {
    setDashboardLoading(true);
    try {
      const res = await axios.get(`${API}/admin/payment-dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboardData(res.data);
    } catch (e) {
      toast.error('Errore caricamento dashboard');
    } finally {
      setDashboardLoading(false);
    }
  };

  const fetchCoupons = async () => {
    try {
      const res = await axios.get(`${API}/admin/coupons`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCoupons(res.data);
    } catch (e) {
      toast.error('Errore caricamento codici sconto');
    }
  };

  const handleCreateCoupon = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        code: couponFormData.code.trim().toUpperCase(),
        discount_percent: parseInt(couponFormData.discount_percent),
        max_uses: couponFormData.max_uses ? parseInt(couponFormData.max_uses) : null,
        product_slug: couponFormData.product_slug || null,
        expires_at: couponFormData.expires_at ? new Date(couponFormData.expires_at).toISOString() : null,
      };
      await axios.post(`${API}/admin/coupons`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Codice creato');
      setShowCouponForm(false);
      setCouponFormData({ code: '', discount_percent: 100, max_uses: '', product_slug: '', expires_at: '' });
      fetchCoupons();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore');
    }
  };

  const toggleCoupon = async (code) => {
    try {
      await axios.patch(`${API}/admin/coupons/${code}/toggle`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchCoupons();
    } catch (e) {
      toast.error('Errore');
    }
  };

  const deleteCouponItem = async (code) => {
    if (!window.confirm(`Eliminare il codice ${code}?`)) return;
    try {
      await axios.delete(`${API}/admin/coupons/${code}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Codice eliminato');
      fetchCoupons();
    } catch (e) {
      toast.error('Errore');
    }
  };

  const fetchConsentStats = async () => {
    setConsentLoading(true);
    try {
      const res = await axios.get(`${API}/admin/consent-stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConsentStats(res.data);
    } catch (e) {
      toast.error('Errore caricamento statistiche cookie');
    } finally {
      setConsentLoading(false);
    }
  };

  const fetchSiteStats = async (month) => {
    setSiteStatsLoading(true);
    try {
      const res = await axios.get(`${API}/admin/stats`, {
        params: { month: month || statsMonth },
        headers: { Authorization: `Bearer ${token}` }
      });
      setSiteStats(res.data);
    } catch (e) {
      toast.error('Errore caricamento statistiche sito');
    } finally {
      setSiteStatsLoading(false);
    }
  };

  const fetchCheckupStats = async () => {
    setCheckupLoading(true);
    try {
      const res = await axios.get(`${API}/admin/checkup-stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCheckupStats(res.data);
    } catch (e) {
      toast.error('Errore caricamento statistiche check-up');
    } finally {
      setCheckupLoading(false);
    }
  };

  const fetchAbandonedLeads = async () => {
    setAbandonedLoading(true);
    try {
      const res = await axios.get(`${API}/admin/checkup-abandoned`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAbandonedLeads(res.data);
    } catch (e) {
      toast.error('Errore caricamento abbandoni');
    } finally {
      setAbandonedLoading(false);
    }
  };

  const fetchForfettari = async () => {
    setForfLoading(true);
    try {
      const res = await axios.get(`${API}/admin/forfettari/riepilogo`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setForfettari(res.data);
    } catch (e) {
      toast.error('Errore caricamento riepilogo forfettari');
    } finally {
      setForfLoading(false);
    }
  };

  const fetchFeatureFlags = async () => {
    try {
      const res = await axios.get(`${API}/admin/config/flags`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFeatureFlags(res.data);
    } catch (e) {
      toast.error('Errore caricamento flag');
    }
  };

  const toggleFlag = async (key, enabled) => {
    try {
      await axios.post(`${API}/admin/config/flags/${key}`, { enabled }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFeatureFlags((f) => ({ ...(f || {}), [key]: enabled }));
      toast.success(enabled ? 'Riattivato' : 'Sospeso');
    } catch (e) {
      toast.error('Errore');
    }
  };

  const downloadLeadsCsv = async () => {
    try {
      const res = await axios.get(`${API}/admin/checkup-leads/export.csv`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `checkup-leads-${new Date().toISOString().slice(0,16).replace(/[:T]/g,'-')}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Errore download CSV');
    }
  };

  const [resendModal, setResendModal] = useState(null); // {purchase, product_version}

  const openResendModal = (purchase) => {
    setResendModal(purchase);
  };

  const closeResendModal = () => setResendModal(null);

  const confirmResend = async (reason) => {
    if (!resendModal) return;
    const p = resendModal;
    try {
      await axios.post(
        `${API}/admin/purchases/${p.id}/resend-email`,
        { reason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(reason === 'update'
        ? `Email di aggiornamento inviata a ${p.customer_email}`
        : `Email reinviata a ${p.customer_email}`);
      closeResendModal();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore invio email');
    }
  };

  const updateProductVersion = async (slug, currentVersion) => {
    const version = window.prompt(`Inserisci la nuova versione per "${slug}":`, currentVersion || 'v1');
    if (!version || version === currentVersion) return;
    try {
      await axios.patch(
        `${API}/admin/products/${slug}/version`,
        { version },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Versione aggiornata a ${version}`);
      fetchDashboard();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore aggiornamento');
    }
  };

  const resendDeliveryEmail = async (purchaseId, email) => {
    // Deprecated helper kept for compatibility, no longer used directly
    return openResendModal({ id: purchaseId, customer_email: email });
  };

  // Login Screen
  if (!isLoggedIn) {
    return (
      <main className="min-h-screen bg-black flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-gradient-to-br from-gold to-yellow-600 rounded mx-auto mb-4 flex items-center justify-center">
              <Lock className="text-black" size={32} />
            </div>
            <h1 className="font-heading text-4xl text-white">ADMIN PANEL</h1>
            <p className="text-white/50 mt-2">Gestione Blog SC Dottori Commercialisti</p>
          </div>
          
          <form onSubmit={handleLogin} className="bg-white/5 border border-white/10 rounded-lg p-8">
            <div className="space-y-6">
              <div>
                <label className="block text-sm text-white/70 mb-2">Username</label>
                <input
                  type="text"
                  required
                  value={loginData.username}
                  onChange={(e) => setLoginData({...loginData, username: e.target.value})}
                  className="w-full bg-white/5 border border-white/20 focus:border-gold rounded px-4 py-3 text-white outline-none"
                  data-testid="admin-username"
                />
              </div>
              <div>
                <label className="block text-sm text-white/70 mb-2">Password</label>
                <input
                  type="password"
                  required
                  value={loginData.password}
                  onChange={(e) => setLoginData({...loginData, password: e.target.value})}
                  className="w-full bg-white/5 border border-white/20 focus:border-gold rounded px-4 py-3 text-white outline-none"
                  data-testid="admin-password"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gold text-black hover:bg-yellow-500 py-4 rounded font-bold transition-all disabled:opacity-50"
                data-testid="admin-login-btn"
              >
                {loading ? 'Accesso...' : 'ACCEDI'}
              </button>
            </div>
          </form>
          
          <p className="text-center text-white/30 text-sm mt-6">
            <Link to="/" className="hover:text-white">← Torna al sito</Link>
          </p>
        </div>
      </main>
    );
  }

  // Admin Dashboard
  return (
    <main className="min-h-screen bg-black pt-6 pb-20">
      <div className="max-w-6xl mx-auto px-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gradient-to-br from-gold to-yellow-600 rounded flex items-center justify-center">
              <span className="font-heading text-black text-xl font-bold">SC</span>
            </div>
            <div>
              <h1 className="font-heading text-2xl text-white">ADMIN PANEL</h1>
              <p className="text-white/50 text-sm">Gestione contenuti e pagamenti</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/approfondimenti" target="_blank" className="text-white/50 hover:text-gold text-sm">
              Vedi Blog →
            </Link>
            <button onClick={handleLogout} className="flex items-center gap-2 text-white/50 hover:text-netflix transition-colors">
              <LogOut size={18} /> Esci
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-8 border-b border-white/10 pb-2">
          <button
            onClick={() => { setAdminTab('articles'); fetchArticles(); }}
            className={`px-6 py-3 rounded-t font-bold text-sm transition-all ${adminTab === 'articles' ? 'bg-gold text-black' : 'text-white/50 hover:text-white'}`}
            data-testid="admin-tab-articles"
          >
            Articoli Blog
          </button>
          <button
            onClick={() => { setAdminTab('payments'); fetchPaymentLinks(); }}
            className={`px-6 py-3 rounded-t font-bold text-sm transition-all ${adminTab === 'payments' ? 'bg-gold text-black' : 'text-white/50 hover:text-white'}`}
            data-testid="admin-tab-payments"
          >
            Link di Pagamento
          </button>
          <button
            onClick={() => { setAdminTab('dashboard'); fetchDashboard(); }}
            className={`px-6 py-3 rounded-t font-bold text-sm transition-all ${adminTab === 'dashboard' ? 'bg-gold text-black' : 'text-white/50 hover:text-white'}`}
            data-testid="admin-tab-dashboard"
          >
            Dashboard Pagamenti
          </button>
          <button
            onClick={() => { setAdminTab('newsletter'); fetchNewsletterData(); }}
            className={`px-6 py-3 rounded-t font-bold text-sm transition-all ${adminTab === 'newsletter' ? 'bg-gold text-black' : 'text-white/50 hover:text-white'}`}
            data-testid="admin-tab-newsletter"
          >
            Newsletter
          </button>
          <button
            onClick={() => { setAdminTab('coupons'); fetchCoupons(); }}
            className={`px-6 py-3 rounded-t font-bold text-sm transition-all ${adminTab === 'coupons' ? 'bg-gold text-black' : 'text-white/50 hover:text-white'}`}
            data-testid="admin-tab-coupons"
          >
            Codici Sconto
          </button>
          <button
            onClick={() => { setAdminTab('consent'); fetchConsentStats(); }}
            className={`px-6 py-3 rounded-t font-bold text-sm transition-all ${adminTab === 'consent' ? 'bg-gold text-black' : 'text-white/50 hover:text-white'}`}
            data-testid="admin-tab-consent"
          >
            Cookie Consent
          </button>
          <button
            onClick={() => setAdminTab('report97')}
            className={`px-6 py-3 rounded-t font-bold text-sm transition-all ${adminTab === 'report97' ? 'bg-gold text-black' : 'text-white/50 hover:text-white'}`}
            data-testid="admin-tab-report97"
          >
            Report 97 €
          </button>
          <button
            onClick={() => { setAdminTab('stats'); fetchSiteStats(); }}
            className={`px-6 py-3 rounded-t font-bold text-sm transition-all ${adminTab === 'stats' ? 'bg-gold text-black' : 'text-white/50 hover:text-white'}`}
            data-testid="admin-tab-stats"
          >
            Statistiche
          </button>
          <button
            onClick={() => { setAdminTab('checkup'); fetchCheckupStats(); fetchFeatureFlags(); }}
            className={`px-6 py-3 rounded-t font-bold text-sm transition-all ${adminTab === 'checkup' ? 'bg-gold text-black' : 'text-white/50 hover:text-white'}`}
            data-testid="admin-tab-checkup"
          >
            Check-up Analytics
          </button>
          <button
            onClick={() => { setAdminTab('abandoned'); fetchAbandonedLeads(); }}
            className={`px-6 py-3 rounded-t font-bold text-sm transition-all ${adminTab === 'abandoned' ? 'bg-gold text-black' : 'text-white/50 hover:text-white'}`}
            data-testid="admin-tab-abandoned"
          >
            Abbandoni Check-up
          </button>
          <button
            onClick={() => { setAdminTab('forfettari'); fetchForfettari(); }}
            className={`px-6 py-3 rounded-t font-bold text-sm transition-all ${adminTab === 'forfettari' ? 'bg-gold text-black' : 'text-white/50 hover:text-white'}`}
            data-testid="admin-tab-forfettari"
          >
            Forfettari
          </button>
          <button
            onClick={() => setAdminTab('seo')}
            className={`px-6 py-3 rounded-t font-bold text-sm transition-all ${adminTab === 'seo' ? 'bg-gold text-black' : 'text-white/50 hover:text-white'}`}
            data-testid="admin-tab-seo"
          >
            Istantanee SEO
          </button>
          <button
            onClick={() => setAdminTab('qmdp')}
            className={`px-6 py-3 rounded-t font-bold text-sm transition-all ${adminTab === 'qmdp' ? 'bg-gold text-black' : 'text-white/50 hover:text-white'}`}
            data-testid="admin-tab-qmdp"
          >
            Quanto mettere da parte
          </button>
        </div>

        {/* ARTICLES TAB */}
        {adminTab === 'articles' && (<>
        {/* Actions */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-heading text-xl text-white">
            {showForm ? (editingArticle ? 'MODIFICA ARTICOLO' : 'NUOVO ARTICOLO') : 'ARTICOLI'}
          </h2>
          {!showForm && (
            <div className="flex items-center gap-3">
              <label
                className={`flex items-center gap-2 bg-white/10 border border-gold/40 text-gold hover:bg-gold hover:text-black px-5 py-3 rounded font-bold transition-all cursor-pointer ${importingHtml ? 'opacity-50 pointer-events-none' : ''}`}
                data-testid="import-html-btn"
                title="Carica un articolo HTML completo: pubblica da solo con menu, condivisione, newsletter, correlati, tracking e anteprima social"
              >
                <Download size={18} />
                {importingHtml ? 'Importo...' : 'Importa Articolo HTML'}
                <input
                  type="file"
                  accept=".html,.htm"
                  className="hidden"
                  onChange={handleImportHtml}
                  data-testid="import-html-input"
                />
              </label>
              <button
                onClick={() => setShowForm(true)}
                className="flex items-center gap-2 bg-gold text-black hover:bg-yellow-500 px-6 py-3 rounded font-bold transition-all"
                data-testid="new-article-btn"
              >
                <Plus size={18} />
                Nuovo Articolo
              </button>
            </div>
          )}
        </div>

        {/* Form */}
        {showForm && (
          <div className="bg-white/5 border border-white/10 rounded-lg p-8 mb-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm text-white/70 mb-2">Titolo *</label>
                  <input
                    type="text"
                    required
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    className="w-full bg-white/5 border border-white/20 focus:border-gold rounded px-4 py-3 text-white outline-none"
                    placeholder="Es: Come ridurre le tasse legalmente"
                    data-testid="article-title"
                  />
                </div>
                <div>
                  <label className="block text-sm text-white/70 mb-2">Categoria *</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({...formData, category: e.target.value})}
                    className="w-full bg-white/5 border border-white/20 focus:border-gold rounded px-4 py-3 text-white outline-none"
                    data-testid="article-category"
                  >
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm text-white/70 mb-2">Anteprima (breve descrizione) *</label>
                <textarea
                  required
                  rows={2}
                  value={formData.excerpt}
                  onChange={(e) => setFormData({...formData, excerpt: e.target.value})}
                  className="w-full bg-white/5 border border-white/20 focus:border-gold rounded px-4 py-3 text-white outline-none resize-none"
                  placeholder="Una breve descrizione che appare nella lista articoli..."
                  data-testid="article-excerpt"
                />
              </div>

              <div>
                <label className="block text-sm text-white/70 mb-2">Contenuto completo *</label>
                <textarea
                  required
                  rows={10}
                  value={formData.content}
                  onChange={(e) => setFormData({...formData, content: e.target.value})}
                  className="w-full bg-white/5 border border-white/20 focus:border-gold rounded px-4 py-3 text-white outline-none resize-none"
                  placeholder="Scrivi qui il contenuto dell'articolo..."
                  data-testid="article-content"
                />
                <p className="text-white/40 text-xs mt-1">Supporta Markdown: ## Titolo, **grassetto**, elenchi con - o 1., tabelle. Puoi incollare direttamente un file .md</p>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm text-white/70 mb-2">Immagine di copertina</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={formData.image_url}
                      onChange={(e) => setFormData({...formData, image_url: e.target.value})}
                      className="flex-1 bg-white/5 border border-white/20 focus:border-gold rounded px-4 py-3 text-white outline-none"
                      placeholder="Carica un file o incolla un URL..."
                      data-testid="article-image"
                    />
                    <label className={`flex items-center gap-2 bg-white/10 hover:bg-gold hover:text-black border border-white/20 px-4 py-3 rounded font-bold text-sm cursor-pointer transition-all whitespace-nowrap ${uploadingImage ? 'opacity-50 pointer-events-none' : ''}`} data-testid="upload-image-btn">
                      <Image size={16} />
                      {uploadingImage ? 'Carico...' : 'Carica'}
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp,image/gif"
                        onChange={handleImageUpload}
                        className="hidden"
                        data-testid="upload-image-input"
                      />
                    </label>
                  </div>
                  <p className="text-white/40 text-xs mt-1">Carica un'immagine dal tuo computer (jpg, png, webp — max 8 MB) oppure incolla un URL</p>
                </div>
                <div>
                  <label className="block text-sm text-white/70 mb-2">Autore</label>
                  <input
                    type="text"
                    value={formData.author}
                    onChange={(e) => setFormData({...formData, author: e.target.value})}
                    className="w-full bg-white/5 border border-white/20 focus:border-gold rounded px-4 py-3 text-white outline-none"
                    data-testid="article-author"
                  />
                </div>
              </div>

              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="is_published"
                  checked={formData.is_published}
                  onChange={(e) => setFormData({...formData, is_published: e.target.checked})}
                  className="w-5 h-5 rounded border-white/20 bg-white/5 text-gold"
                />
                <label htmlFor="is_published" className="text-white/70">Pubblica subito</label>
              </div>

              <div className="flex items-center gap-4 pt-4">
                <button
                  type="submit"
                  disabled={loading}
                  className="flex items-center gap-2 bg-gold text-black hover:bg-yellow-500 px-8 py-3 rounded font-bold transition-all disabled:opacity-50"
                  data-testid="save-article-btn"
                >
                  <Save size={18} />
                  {loading ? 'Salvataggio...' : (editingArticle ? 'Aggiorna' : 'Pubblica')}
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  className="text-white/50 hover:text-white transition-colors"
                >
                  Annulla
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Articles List */}
        {!showForm && (
          <div className="space-y-4">
            {loading ? (
              <div className="text-center py-12">
                <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin mx-auto"></div>
              </div>
            ) : articles.length === 0 ? (
              <div className="text-center py-12 bg-white/5 border border-white/10 rounded-lg">
                <Image className="mx-auto text-white/20 mb-4" size={48} />
                <p className="text-white/50">Nessun articolo ancora.</p>
                <button
                  onClick={() => setShowForm(true)}
                  className="text-gold hover:underline mt-2"
                >
                  Crea il primo articolo
                </button>
              </div>
            ) : (
              articles.map((article) => (
                <div
                  key={article.id}
                  className="flex items-center gap-6 bg-white/5 border border-white/10 rounded-lg p-4 hover:border-white/20 transition-all"
                  data-testid={`admin-article-${article.id}`}
                >
                  {article.image_url && (
                    <img
                      src={article.image_url}
                      alt=""
                      className="w-24 h-16 object-cover rounded"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-1">
                      <span className="text-xs font-bold text-gold bg-gold/10 px-2 py-0.5 rounded">
                        {article.category}
                      </span>
                      {!article.is_published && (
                        <span className="text-xs font-bold text-netflix bg-netflix/10 px-2 py-0.5 rounded">
                          BOZZA
                        </span>
                      )}
                    </div>
                    <h3 className="font-medium text-white truncate">{article.title}</h3>
                    <p className="text-white/50 text-sm truncate">{article.excerpt}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleSendNewsletter(article)}
                      className="p-2 text-white/50 hover:text-gold transition-colors"
                      title="Invia newsletter agli iscritti"
                      data-testid={`send-newsletter-${article.id}`}
                    >
                      <Mail size={18} />
                    </button>
                    <button
                      onClick={() => handleEdit(article)}
                      className="p-2 text-white/50 hover:text-gold transition-colors"
                      title="Modifica"
                    >
                      <Edit size={18} />
                    </button>
                    <button
                      onClick={() => handleDelete(article.id)}
                      className="p-2 text-white/50 hover:text-netflix transition-colors"
                      title="Elimina"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
        </>)}

        {/* PAYMENTS TAB */}
        {adminTab === 'payments' && (
          <>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-xl text-white">LINK DI PAGAMENTO</h2>
              {!showPaymentForm && (
                <button
                  onClick={() => setShowPaymentForm(true)}
                  className="flex items-center gap-2 bg-gold text-black hover:bg-yellow-500 px-6 py-3 rounded font-bold transition-all"
                  data-testid="new-payment-btn"
                >
                  <Plus size={18} />
                  Nuovo Link
                </button>
              )}
            </div>

            {showPaymentForm && (
              <div className="bg-white/5 border border-white/10 rounded-lg p-8 mb-8">
                <form onSubmit={handleCreatePaymentLink} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm text-white/70 mb-2">Titolo *</label>
                      <input
                        type="text"
                        required
                        value={paymentFormData.title}
                        onChange={(e) => setPaymentFormData({...paymentFormData, title: e.target.value})}
                        className="w-full bg-white/5 border border-white/20 focus:border-gold rounded px-4 py-3 text-white outline-none"
                        placeholder="Es: Consulenza strategica mensile"
                        data-testid="payment-title"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-white/70 mb-2">Importo (EUR) *</label>
                      <input
                        type="number"
                        required
                        step="0.01"
                        min="1"
                        value={paymentFormData.amount}
                        onChange={(e) => setPaymentFormData({...paymentFormData, amount: e.target.value})}
                        className="w-full bg-white/5 border border-white/20 focus:border-gold rounded px-4 py-3 text-white outline-none"
                        placeholder="100.00"
                        data-testid="payment-amount"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm text-white/70 mb-2">Descrizione</label>
                    <input
                      type="text"
                      value={paymentFormData.description}
                      onChange={(e) => setPaymentFormData({...paymentFormData, description: e.target.value})}
                      className="w-full bg-white/5 border border-white/20 focus:border-gold rounded px-4 py-3 text-white outline-none"
                      placeholder="Descrizione opzionale del servizio"
                    />
                  </div>

                  <div>
                    <div>
                      <label className="block text-sm text-white/70 mb-2">Tipo di Pagamento *</label>
                      <select
                        value={paymentFormData.payment_type}
                        onChange={(e) => setPaymentFormData({...paymentFormData, payment_type: e.target.value})}
                        className="w-full bg-[#111] border border-white/20 focus:border-gold rounded px-4 py-3 text-white outline-none"
                        data-testid="payment-type"
                      >
                        <option value="one_time">Pagamento unico</option>
                        <option value="recurring">Abbonamento mensile</option>
                      </select>
                    </div>
                  </div>

                  <div className="bg-white/5 border border-white/10 rounded p-4 text-sm text-white/60">
                    {paymentFormData.payment_type === 'one_time' && '↳ Il cliente paga una volta sola. Stripe + PayPal disponibili.'}
                    {paymentFormData.payment_type === 'recurring' && '↳ Il cliente paga ogni mese fino a disdetta. Solo Stripe.'}
                  </div>

                  <div className="flex items-center gap-4 pt-4">
                    <button type="submit" disabled={loading} className="flex items-center gap-2 bg-gold text-black hover:bg-yellow-500 px-8 py-3 rounded font-bold transition-all disabled:opacity-50" data-testid="save-payment-btn">
                      <Save size={18} />
                      {loading ? 'Creazione...' : 'Crea Link'}
                    </button>
                    <button type="button" onClick={() => setShowPaymentForm(false)} className="text-white/50 hover:text-white transition-colors">Annulla</button>
                  </div>
                </form>
              </div>
            )}

            {!showPaymentForm && (
              <div className="space-y-4">
                {paymentLinks.length === 0 ? (
                  <div className="text-center py-12 bg-white/5 border border-white/10 rounded-lg">
                    <Zap className="mx-auto text-white/20 mb-4" size={48} />
                    <p className="text-white/50">Nessun link di pagamento.</p>
                    <button onClick={() => setShowPaymentForm(true)} className="text-gold hover:underline mt-2">Crea il primo link</button>
                  </div>
                ) : (
                  paymentLinks.map((link) => (
                    <div key={link.id} className="bg-white/5 border border-white/10 rounded-lg p-5 hover:border-white/20 transition-all" data-testid={`payment-link-${link.id}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="font-medium text-white">{link.title}</h3>
                            <span className={`text-xs font-bold px-2 py-0.5 rounded ${link.is_active ? 'text-green-400 bg-green-400/10' : 'text-red-400 bg-red-400/10'}`}>
                              {link.is_active ? 'ATTIVO' : 'DISATTIVO'}
                            </span>
                            <span className="text-xs font-bold text-gold bg-gold/10 px-2 py-0.5 rounded">
                              {link.payment_type === 'one_time' ? 'UNA TANTUM' : 'ABBONAMENTO'}
                            </span>
                          </div>
                          <div className="flex items-center gap-4 text-sm text-white/50">
                            <span className="font-heading text-2xl text-gold">{link.amount.toFixed(2)} EUR</span>
                            {link.payment_type === 'recurring' && <span>/ mese</span>}
                          </div>
                          {link.description && <p className="text-white/40 text-sm mt-1">{link.description}</p>}
                        </div>
                        <div className="flex items-center gap-2 ml-4">
                          <button
                            onClick={() => copyLink(link.slug)}
                            className="flex items-center gap-2 bg-gold/10 text-gold hover:bg-gold hover:text-black px-4 py-2 rounded text-sm font-bold transition-all"
                            data-testid={`copy-link-${link.id}`}
                          >
                            Copia Link
                          </button>
                          <button onClick={() => handleTogglePaymentLink(link.id)} className="p-2 text-white/50 hover:text-gold transition-colors" title={link.is_active ? 'Disattiva' : 'Attiva'}>
                            {link.is_active ? <Eye size={18} /> : <Lock size={18} />}
                          </button>
                          <button onClick={() => handleRenameSlug(link.id, link.slug)} className="p-2 text-white/50 hover:text-gold transition-colors" title="Rinomina slug (URL)" data-testid={`rename-slug-${link.id}`}>
                            <Edit size={18} />
                          </button>
                          <button onClick={() => handleDeletePaymentLink(link.id)} className="p-2 text-white/50 hover:text-netflix transition-colors" title="Elimina">
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </>
        )}

        {/* DASHBOARD TAB */}
        {adminTab === 'dashboard' && (
          <>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-xl text-white">DASHBOARD PAGAMENTI</h2>
              <button
                onClick={fetchDashboard}
                className="flex items-center gap-2 bg-white/10 text-white hover:bg-white/20 px-4 py-2 rounded text-sm font-medium transition-all"
                data-testid="refresh-dashboard"
              >
                <TrendingUp size={16} /> Aggiorna
              </button>
            </div>

            {dashboardLoading && (
              <div className="flex justify-center py-12">
                <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin"></div>
              </div>
            )}

            {!dashboardLoading && dashboardData && (
              <div className="space-y-8">
                {/* Stats Grid */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="dashboard-stats">
                  <div className="bg-white/5 border border-white/10 rounded-lg p-5">
                    <p className="text-white/50 text-xs tracking-wider mb-1">ABBONAMENTI ATTIVI</p>
                    <p className="font-heading text-3xl text-green-400">{dashboardData.stats.active_subscriptions}</p>
                  </div>
                  <div className="bg-white/5 border border-white/10 rounded-lg p-5">
                    <p className="text-white/50 text-xs tracking-wider mb-1">RICAVO MENSILE</p>
                    <p className="font-heading text-3xl text-gold">{dashboardData.stats.monthly_recurring_revenue.toFixed(2)} <span className="text-lg">EUR</span></p>
                  </div>
                  <div className="bg-white/5 border border-white/10 rounded-lg p-5">
                    <p className="text-white/50 text-xs tracking-wider mb-1">INSOLUTI</p>
                    <p className={`font-heading text-3xl ${dashboardData.stats.past_due_subscriptions > 0 ? 'text-netflix' : 'text-white/30'}`}>{dashboardData.stats.past_due_subscriptions}</p>
                  </div>
                  <div className="bg-white/5 border border-white/10 rounded-lg p-5">
                    <p className="text-white/50 text-xs tracking-wider mb-1">DISDETTE</p>
                    <p className="font-heading text-3xl text-white/40">{dashboardData.stats.canceled_subscriptions}</p>
                  </div>
                </div>

                {/* Subscriptions */}
                <div>
                  <h3 className="font-heading text-lg text-white mb-4">ABBONAMENTI</h3>
                  {dashboardData.subscriptions.length === 0 ? (
                    <div className="text-center py-8 bg-white/5 border border-white/10 rounded-lg">
                      <p className="text-white/40">Nessun abbonamento attivo. Quando un cliente si abbona, lo vedrai qui.</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {dashboardData.subscriptions.map((sub, i) => {
                        const startDate = new Date(sub.created * 1000).toLocaleDateString('it-IT', { day: 'numeric', month: 'short', year: 'numeric' });
                        const periodEnd = new Date(sub.current_period_end * 1000).toLocaleDateString('it-IT', { day: 'numeric', month: 'short', year: 'numeric' });
                        const now = Date.now() / 1000;
                        const durationMonths = Math.round((now - sub.created) / (30 * 86400));

                        const statusColors = {
                          active: 'text-green-400 bg-green-400/10',
                          past_due: 'text-red-400 bg-red-400/10',
                          canceled: 'text-white/40 bg-white/5',
                          unpaid: 'text-orange-400 bg-orange-400/10',
                          trialing: 'text-blue-400 bg-blue-400/10',
                        };
                        const statusLabels = {
                          active: 'ATTIVO',
                          past_due: 'INSOLUTO',
                          canceled: 'DISDETTO',
                          unpaid: 'NON PAGATO',
                          trialing: 'PROVA',
                          incomplete: 'INCOMPLETO',
                          incomplete_expired: 'SCADUTO',
                          paused: 'IN PAUSA',
                        };

                        return (
                          <div key={sub.subscription_id || i} className="bg-white/5 border border-white/10 rounded-lg p-5" data-testid={`subscription-${i}`}>
                            <div className="flex items-center justify-between flex-wrap gap-3">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-3 mb-2">
                                  <h4 className="font-medium text-white">{sub.payment_link_title}</h4>
                                  <span className={`text-xs font-bold px-2 py-0.5 rounded ${statusColors[sub.status] || 'text-white/50 bg-white/5'}`}>
                                    {statusLabels[sub.status] || sub.status.toUpperCase()}
                                  </span>
                                </div>
                                <div className="flex items-center gap-6 text-sm text-white/50">
                                  <span className="font-heading text-xl text-gold">{sub.amount.toFixed(2)} EUR<span className="text-sm text-white/40">/mese</span></span>
                                  {sub.customer_email && <span>{sub.customer_email}</span>}
                                </div>
                              </div>
                              <div className="text-right text-sm">
                                <p className="text-white/60">Attivo da: <span className="text-white">{startDate}</span></p>
                                <p className="text-white/40">Durata: <span className="text-gold font-bold">{durationMonths} {durationMonths === 1 ? 'mese' : 'mesi'}</span></p>
                                <p className="text-white/40">Prossimo rinnovo: <span className="text-white/60">{periodEnd}</span></p>
                                {sub.cancel_at_period_end && <p className="text-netflix text-xs font-bold mt-1">Disdetta a fine periodo</p>}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Recent Transactions */}
                <div>
                  <h3 className="font-heading text-lg text-white mb-4">ULTIME TRANSAZIONI</h3>
                  {dashboardData.recent_transactions.length === 0 ? (
                    <div className="text-center py-8 bg-white/5 border border-white/10 rounded-lg">
                      <p className="text-white/40">Nessuna transazione ancora.</p>
                    </div>
                  ) : (
                    <div className="bg-white/5 border border-white/10 rounded-lg overflow-x-auto">
                      <table className="w-full text-sm" data-testid="transactions-table">
                        <thead>
                          <tr className="border-b border-white/10">
                            <th className="text-left text-white/50 text-xs tracking-wider p-4">SERVIZIO</th>
                            <th className="text-left text-white/50 text-xs tracking-wider p-4">CLIENTE</th>
                            <th className="text-left text-white/50 text-xs tracking-wider p-4">CF / P.IVA</th>
                            <th className="text-left text-white/50 text-xs tracking-wider p-4">IMPORTO</th>
                            <th className="text-left text-white/50 text-xs tracking-wider p-4">GATEWAY</th>
                            <th className="text-left text-white/50 text-xs tracking-wider p-4">STATO</th>
                            <th className="text-left text-white/50 text-xs tracking-wider p-4">DATA</th>
                          </tr>
                        </thead>
                        <tbody>
                          {dashboardData.recent_transactions.map((tx, i) => {
                            const txDate = tx.created_at ? new Date(tx.created_at).toLocaleDateString('it-IT', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
                            const statusMap = { paid: 'PAGATO', pending: 'IN ATTESA', initiated: 'AVVIATO', failed: 'FALLITO' };
                            const statusColor = { paid: 'text-green-400', pending: 'text-yellow-400', initiated: 'text-white/40', failed: 'text-red-400' };
                            return (
                              <tr key={tx.id || i} className="border-b border-white/5 hover:bg-white/5">
                                <td className="p-4">
                                  <div className="text-white">{tx.link_title}</div>
                                  <div className="text-white/30 text-xs">{tx.link_type === 'one_time' ? 'Una tantum' : 'Abbonamento'}</div>
                                </td>
                                <td className="p-4">
                                  <div className="text-white">{tx.billing_name || '—'}</div>
                                  <div className="text-white/40 text-xs">{tx.billing_email || '—'}</div>
                                  <span className={`text-xs px-1.5 py-0.5 rounded ${tx.billing_type === 'azienda' ? 'text-blue-400 bg-blue-400/10' : 'text-white/40 bg-white/5'}`}>
                                    {tx.billing_type === 'azienda' ? 'Azienda' : tx.billing_type === 'privato' ? 'Privato' : '—'}
                                  </span>
                                  <div className="text-gold/70 text-xs mt-1">Origine: {tx.attr?.label || 'non rilevata'}</div>
                                </td>
                                <td className="p-4">
                                  <div className="text-white/70 text-xs">{tx.billing_cf || '—'}</div>
                                  {tx.billing_piva && <div className="text-white/50 text-xs">{tx.billing_piva}</div>}
                                </td>
                                <td className="p-4 text-gold font-bold whitespace-nowrap">{tx.amount.toFixed(2)} {tx.currency.toUpperCase()}</td>
                                <td className="p-4 text-white/60 capitalize">{tx.provider}</td>
                                <td className={`p-4 font-bold ${statusColor[tx.payment_status] || 'text-white/40'}`}>{statusMap[tx.payment_status] || tx.payment_status.toUpperCase()}</td>
                                <td className="p-4 text-white/40 whitespace-nowrap">{txDate}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                {/* === VERSIONI PRODOTTI === */}
                {dashboardData.digital_products && dashboardData.digital_products.length > 0 && (
                  <div className="mb-8">
                    <h3 className="font-heading text-lg text-white mb-4 tracking-wide">VERSIONI PRODOTTI DIGITALI</h3>
                    <p className="text-white/40 text-xs mb-4">
                      Quando aggiorni la versione qui, verrà mostrata come "aggiornamento" nel bottone REINVIA EMAIL.
                      Ricordati di caricare il nuovo file Excel prima di aggiornare l'etichetta.
                    </p>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {dashboardData.digital_products.map((prod) => (
                        <div key={prod.slug} className="bg-white/5 border border-white/10 rounded-lg p-4 flex items-center justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <p className="text-white text-sm font-semibold truncate">{prod.title}</p>
                            <p className="text-white/40 text-xs">
                              Versione: <span className="text-gold font-mono font-bold">{prod.current_version || '—'}</span>
                            </p>
                          </div>
                          <button
                            onClick={() => updateProductVersion(prod.slug, prod.current_version)}
                            className="text-gold hover:text-yellow-400 text-xs font-bold tracking-wider whitespace-nowrap"
                            data-testid={`edit-version-${prod.slug}`}
                          >
                            MODIFICA
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* === ACQUISTI PRODOTTI DIGITALI === */}
                <div className="mb-8">
                  <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                    <h3 className="font-heading text-lg text-white tracking-wide">ACQUISTI PRODOTTI DIGITALI</h3>
                    <button
                      onClick={handleReconcilePurchases}
                      disabled={reconciling}
                      className="flex items-center gap-2 bg-gold/10 border border-gold/40 hover:bg-gold hover:text-black text-gold px-4 py-2 rounded text-xs font-bold tracking-wider transition-all disabled:opacity-50"
                      data-testid="reconcile-purchases-btn"
                      title="Controlla su Stripe tutti gli acquisti in attesa: quelli realmente pagati vengono confermati e partono le email (cliente + dati fattura a te)"
                    >
                      <RefreshCw size={14} className={reconciling ? 'animate-spin' : ''} />
                      {reconciling ? 'VERIFICO SU STRIPE...' : 'VERIFICA PAGAMENTI SU STRIPE'}
                    </button>
                  </div>
                  {(!dashboardData.product_purchases || dashboardData.product_purchases.length === 0) ? (
                    <div className="text-center py-8 bg-white/5 border border-white/10 rounded-lg">
                      <p className="text-white/50 text-sm">Nessun acquisto prodotti digitali ancora.</p>
                    </div>
                  ) : (
                    <div className="bg-white/5 border border-white/10 rounded-lg overflow-x-auto">
                      <table className="w-full" data-testid="product-purchases-table">
                        <thead className="border-b border-white/10 bg-white/[0.02]">
                          <tr>
                            <th className="text-left text-white/50 text-xs tracking-wider p-3">DATA</th>
                            <th className="text-left text-white/50 text-xs tracking-wider p-3">PRODOTTO</th>
                            <th className="text-left text-white/50 text-xs tracking-wider p-3">CLIENTE</th>
                            <th className="text-left text-white/50 text-xs tracking-wider p-3">FATTURAZIONE</th>
                            <th className="text-right text-white/50 text-xs tracking-wider p-3">IMPORTO</th>
                            <th className="text-center text-white/50 text-xs tracking-wider p-3">STATO</th>
                            <th className="text-center text-white/50 text-xs tracking-wider p-3">AZIONI</th>
                          </tr>
                        </thead>
                        <tbody>
                          {dashboardData.product_purchases.map((p, i) => (
                            <tr key={p.id || i} className="border-b border-white/5 hover:bg-white/5">
                              <td className="p-3 text-white/70 text-xs whitespace-nowrap">
                                {p.created_at ? new Date(p.created_at).toLocaleDateString('it-IT', {day:'2-digit',month:'short',year:'2-digit'}) : '—'}
                              </td>
                              <td className="p-3 text-white text-sm">
                                {p.product_title}
                                {p.product_current_version && (
                                  <span className="ml-2 text-[10px] font-mono text-gold/70 bg-gold/10 px-1.5 py-0.5 rounded">{p.product_current_version}</span>
                                )}
                              </td>
                              <td className="p-3 text-white/80 text-xs">
                                <p className="font-semibold">{p.customer_email}</p>
                                <p className="text-white/40">{p.downloads || 0} download</p>
                                <p className="text-gold/70" data-testid={`purchase-origin-${p.id}`}>
                                  Origine: {p.attr?.label || 'non rilevata'}
                                </p>
                              </td>
                              <td className="p-3 text-white/70 text-xs">
                                <p className="font-semibold text-white">{p.billing_name}</p>
                                <p className="text-white/40">CF: {p.billing_cf}</p>
                                {p.billing_piva && <p className="text-white/40">P.IVA: {p.billing_piva}</p>}
                                <p className="text-white/40">{p.billing_sdi ? `SDI: ${p.billing_sdi}` : (p.billing_pec ? `PEC: ${p.billing_pec}` : '')}</p>
                                <p className="text-white/40">{p.billing_address}, {p.billing_cap} {p.billing_city} ({p.billing_province})</p>
                              </td>
                              <td className="p-3 text-right text-gold font-bold text-sm whitespace-nowrap">
                                {p.amount?.toFixed(2)} €
                                {p.coupon_code && (
                                  <p className="text-[10px] text-white/40 font-normal">
                                    <span className="line-through">{p.original_amount?.toFixed(2)} €</span> · {p.coupon_code}
                                  </p>
                                )}
                              </td>
                              <td className="p-3 text-center">
                                <span className={`text-xs px-2 py-1 rounded font-bold ${p.status === 'paid' ? 'text-green-400 bg-green-400/10' : 'text-yellow-300 bg-yellow-300/10'}`}>
                                  {p.status === 'paid' ? 'PAGATO' : p.status.toUpperCase()}
                                </span>
                              </td>
                              <td className="p-3 text-center whitespace-nowrap">
                                <button
                                  onClick={() => openResendModal(p)}
                                  disabled={p.status !== 'paid' && p.status !== 'pending'}
                                  className="text-gold hover:text-yellow-400 disabled:text-white/20 disabled:cursor-not-allowed text-xs font-bold tracking-wider transition-colors"
                                  data-testid={`resend-email-${p.id}`}
                                  title={p.status === 'pending' ? 'Verifica il pagamento su Stripe e invia l\'email di consegna' : 'Reinvia email al cliente'}
                                >
                                  REINVIA EMAIL
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}

            {!dashboardLoading && !dashboardData && (
              <div className="text-center py-12 bg-white/5 border border-white/10 rounded-lg">
                <BarChart3 className="mx-auto text-white/20 mb-4" size={48} />
                <p className="text-white/50">Caricamento dashboard...</p>
                <button onClick={fetchDashboard} className="text-gold hover:underline mt-2">Ricarica</button>
              </div>
            )}
          </>
        )}

        {/* COUPONS TAB */}
        {adminTab === 'newsletter' && (
          <div data-testid="newsletter-section">
            {!newsletterData ? (
              <div className="flex justify-center py-16"><div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin"></div></div>
            ) : (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                  <div className="bg-white/5 border border-white/10 rounded-lg p-5">
                    <p className="text-white/40 text-xs tracking-widest mb-1">ISCRITTI</p>
                    <p className="font-heading text-3xl text-gold" data-testid="newsletter-total">{newsletterData.active}</p>
                  </div>
                  <div className="bg-white/5 border border-white/10 rounded-lg p-5">
                    <p className="text-white/40 text-xs tracking-widest mb-1">EMAIL INVIATE</p>
                    <p className="font-heading text-3xl text-white">{newsletterData.sends?.length >= 200 ? '200+' : (newsletterData.per_article || []).reduce((a, b) => a + b.sent, 0)}</p>
                  </div>
                  <div className="bg-white/5 border border-white/10 rounded-lg p-5">
                    <p className="text-white/40 text-xs tracking-widest mb-1">APERTE</p>
                    <p className="font-heading text-3xl text-white">{(newsletterData.per_article || []).reduce((a, b) => a + b.opened, 0)}</p>
                  </div>
                  <div className="bg-white/5 border border-white/10 rounded-lg p-5">
                    <p className="text-white/40 text-xs tracking-widest mb-1">CLICK ARTICOLO</p>
                    <p className="font-heading text-3xl text-white">{(newsletterData.per_article || []).reduce((a, b) => a + b.clicked, 0)}</p>
                  </div>
                </div>

                {(newsletterData.per_article || []).length > 0 && (
                  <div className="mb-8">
                    <h3 className="font-heading text-lg text-white mb-4 tracking-wide">STATISTICHE PER ARTICOLO</h3>
                    <div className="bg-white/5 border border-white/10 rounded-lg overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead><tr className="border-b border-white/10 text-white/40 text-xs tracking-wider">
                          <th className="text-left px-4 py-3">ARTICOLO</th><th className="text-right px-4 py-3">INVIATE</th><th className="text-right px-4 py-3">APERTE</th><th className="text-right px-4 py-3">CLICK</th><th className="text-right px-4 py-3">% APERTURA</th>
                        </tr></thead>
                        <tbody>
                          {newsletterData.per_article.map(a => (
                            <tr key={a.article_id} className="border-b border-white/5">
                              <td className="px-4 py-3 text-white">{a.title}</td>
                              <td className="px-4 py-3 text-right text-white/70">{a.sent}</td>
                              <td className="px-4 py-3 text-right text-gold font-bold">{a.opened}</td>
                              <td className="px-4 py-3 text-right text-gold font-bold">{a.clicked}</td>
                              <td className="px-4 py-3 text-right text-white/70">{a.sent ? Math.round(a.opened / a.sent * 100) : 0}%</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                <div className="grid lg:grid-cols-2 gap-8">
                  <div>
                    <h3 className="font-heading text-lg text-white mb-4 tracking-wide">ISCRITTI ({newsletterData.total})</h3>
                    <div className="bg-white/5 border border-white/10 rounded-lg max-h-96 overflow-y-auto">
                      {newsletterData.subscribers.map(s => (
                        <div key={s.id || s.email} className="flex items-center justify-between px-4 py-2.5 border-b border-white/5 text-sm">
                          <span className="text-white">{s.email}</span>
                          <span className="text-white/40 text-xs">{s.subscribed_at ? new Date(s.subscribed_at).toLocaleDateString('it-IT') : ''}{s.active === false ? ' · disattivo' : ''}</span>
                        </div>
                      ))}
                      {newsletterData.subscribers.length === 0 && <p className="text-white/40 text-sm p-4">Nessun iscritto ancora.</p>}
                    </div>
                  </div>
                  <div>
                    <h3 className="font-heading text-lg text-white mb-4 tracking-wide">ULTIMI INVII</h3>
                    <div className="bg-white/5 border border-white/10 rounded-lg max-h-96 overflow-y-auto">
                      {(newsletterData.sends || []).map(s => (
                        <div key={s.id} className="px-4 py-2.5 border-b border-white/5 text-sm flex items-center justify-between gap-2">
                          <div className="min-w-0">
                            <p className="text-white truncate">{s.email}</p>
                            <p className="text-white/30 text-xs truncate">{s.article_title}</p>
                          </div>
                          <div className="flex gap-2 flex-shrink-0 text-xs">
                            <span className={s.opened_at ? 'text-gold font-bold' : 'text-white/25'}>{s.opened_at ? '✓ aperta' : 'non aperta'}</span>
                            <span className={s.clicked_at ? 'text-gold font-bold' : 'text-white/25'}>{s.clicked_at ? '✓ letta' : '—'}</span>
                          </div>
                        </div>
                      ))}
                      {(!newsletterData.sends || newsletterData.sends.length === 0) && <p className="text-white/40 text-sm p-4">Nessun invio tracciato ancora (il tracciamento parte dalle prossime newsletter).</p>}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {adminTab === 'coupons' && (
          <>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-xl text-white">CODICI SCONTO</h2>
              <button
                onClick={() => setShowCouponForm(!showCouponForm)}
                className="flex items-center gap-2 bg-gold text-black hover:bg-yellow-500 px-4 py-2 rounded text-sm font-bold transition-all"
                data-testid="new-coupon-btn"
              >
                <Plus size={16} /> {showCouponForm ? 'Annulla' : 'Nuovo Codice'}
              </button>
            </div>

            {showCouponForm && (
              <form onSubmit={handleCreateCoupon} className="bg-white/5 border border-white/10 rounded-lg p-6 mb-6 space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-white/50 mb-1">Codice *</label>
                    <input type="text" required value={couponFormData.code}
                      onChange={e => setCouponFormData({...couponFormData, code: e.target.value.toUpperCase()})}
                      className="w-full bg-white/5 border border-white/20 focus:border-gold rounded px-4 py-2.5 text-white outline-none uppercase"
                      placeholder="Es: OMAGGIO-AMICO" data-testid="coupon-form-code" />
                  </div>
                  <div>
                    <label className="block text-xs text-white/50 mb-1">Sconto % *</label>
                    <input type="number" min="1" max="100" required value={couponFormData.discount_percent}
                      onChange={e => setCouponFormData({...couponFormData, discount_percent: e.target.value})}
                      className="w-full bg-white/5 border border-white/20 focus:border-gold rounded px-4 py-2.5 text-white outline-none"
                      data-testid="coupon-form-discount" />
                  </div>
                  <div>
                    <label className="block text-xs text-white/50 mb-1">Usi massimi (vuoto = illimitato)</label>
                    <input type="number" min="1" value={couponFormData.max_uses}
                      onChange={e => setCouponFormData({...couponFormData, max_uses: e.target.value})}
                      className="w-full bg-white/5 border border-white/20 focus:border-gold rounded px-4 py-2.5 text-white outline-none"
                      placeholder="Es: 10" />
                  </div>
                  <div>
                    <label className="block text-xs text-white/50 mb-1">Scadenza (opzionale)</label>
                    <input type="date" value={couponFormData.expires_at}
                      onChange={e => setCouponFormData({...couponFormData, expires_at: e.target.value})}
                      className="w-full bg-white/5 border border-white/20 focus:border-gold rounded px-4 py-2.5 text-white outline-none" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-xs text-white/50 mb-1">Prodotto specifico (vuoto = tutti)</label>
                    <input type="text" value={couponFormData.product_slug}
                      onChange={e => setCouponFormData({...couponFormData, product_slug: e.target.value})}
                      className="w-full bg-white/5 border border-white/20 focus:border-gold rounded px-4 py-2.5 text-white outline-none"
                      placeholder="Es: gestore-forfettario" />
                  </div>
                </div>
                <button type="submit" className="bg-gold text-black hover:bg-yellow-500 px-6 py-2.5 rounded font-bold text-sm transition-all" data-testid="coupon-form-submit">
                  Crea Codice
                </button>
              </form>
            )}

            <div className="bg-white/5 border border-white/10 rounded-lg overflow-hidden">
              {coupons.length === 0 ? (
                <div className="text-center py-12">
                  <BadgePercent className="mx-auto text-white/20 mb-4" size={48} />
                  <p className="text-white/50">Nessun codice creato</p>
                </div>
              ) : (
                <table className="w-full" data-testid="coupons-table">
                  <thead className="border-b border-white/10 bg-white/[0.02]">
                    <tr>
                      <th className="text-left text-white/50 text-xs tracking-wider p-4">CODICE</th>
                      <th className="text-left text-white/50 text-xs tracking-wider p-4">SCONTO</th>
                      <th className="text-left text-white/50 text-xs tracking-wider p-4">USI</th>
                      <th className="text-left text-white/50 text-xs tracking-wider p-4">PRODOTTO</th>
                      <th className="text-left text-white/50 text-xs tracking-wider p-4">SCADENZA</th>
                      <th className="text-left text-white/50 text-xs tracking-wider p-4">STATO</th>
                      <th className="text-right text-white/50 text-xs tracking-wider p-4">AZIONI</th>
                    </tr>
                  </thead>
                  <tbody>
                    {coupons.map((c) => {
                      const exhausted = c.max_uses !== null && c.used_count >= c.max_uses;
                      return (
                        <tr key={c.code} className="border-b border-white/5 hover:bg-white/5">
                          <td className="p-4 font-mono text-gold font-bold">{c.code}</td>
                          <td className="p-4 text-white">-{c.discount_percent}%</td>
                          <td className="p-4 text-white/70 text-sm">{c.used_count}{c.max_uses !== null ? ` / ${c.max_uses}` : ''}</td>
                          <td className="p-4 text-white/60 text-sm">{c.product_slug || 'Tutti'}</td>
                          <td className="p-4 text-white/60 text-sm">{c.expires_at ? new Date(c.expires_at).toLocaleDateString('it-IT') : '—'}</td>
                          <td className="p-4">
                            <span className={`text-xs px-2 py-1 rounded font-bold ${c.active && !exhausted ? 'text-green-400 bg-green-400/10' : 'text-white/40 bg-white/5'}`}>
                              {exhausted ? 'ESAURITO' : (c.active ? 'ATTIVO' : 'INATTIVO')}
                            </span>
                          </td>
                          <td className="p-4 text-right">
                            <button onClick={() => toggleCoupon(c.code)} className="text-white/50 hover:text-white text-xs mr-3" data-testid={`coupon-toggle-${c.code}`}>
                              {c.active ? 'Disattiva' : 'Attiva'}
                            </button>
                            <button onClick={() => deleteCouponItem(c.code)} className="text-red-400 hover:text-red-300" data-testid={`coupon-delete-${c.code}`}>
                              <Trash2 size={14} />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}

        {/* COOKIE CONSENT TAB */}
        {adminTab === 'consent' && (
          <div data-testid="consent-stats-panel">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-xl text-white">STATISTICHE COOKIE CONSENT</h2>
              <button
                onClick={fetchConsentStats}
                className="flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 text-white/70 px-4 py-2 rounded text-sm transition-all"
                data-testid="consent-refresh-btn"
              >
                <RefreshCw size={14} /> Aggiorna
              </button>
            </div>

            {consentLoading && !consentStats && (
              <div className="text-center py-16 text-white/50">Caricamento...</div>
            )}

            {consentStats && (
              <>
                {/* KPI Cards */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                  <div className="bg-white/5 border border-white/10 rounded-lg p-5">
                    <p className="text-xs text-white/50 tracking-wider mb-2">TOTALE EVENTI</p>
                    <p className="text-3xl font-heading text-white" data-testid="consent-kpi-total">{consentStats.totals.total}</p>
                    <p className="text-xs text-white/40 mt-2">tutti i tempi</p>
                  </div>
                  <div className="bg-white/5 border border-green-400/20 rounded-lg p-5">
                    <p className="text-xs text-white/50 tracking-wider mb-2">ACCETTATI</p>
                    <p className="text-3xl font-heading text-green-400" data-testid="consent-kpi-granted">{consentStats.totals.granted}</p>
                    <p className="text-xs text-white/40 mt-2">30gg: {consentStats.last_30_days.granted}</p>
                  </div>
                  <div className="bg-white/5 border border-red-400/20 rounded-lg p-5">
                    <p className="text-xs text-white/50 tracking-wider mb-2">RIFIUTATI</p>
                    <p className="text-3xl font-heading text-red-400" data-testid="consent-kpi-denied">{consentStats.totals.denied}</p>
                    <p className="text-xs text-white/40 mt-2">30gg: {consentStats.last_30_days.denied}</p>
                  </div>
                  <div className="bg-gradient-to-br from-gold/10 to-transparent border border-gold/30 rounded-lg p-5">
                    <p className="text-xs text-white/50 tracking-wider mb-2">TASSO ACCETTAZIONE</p>
                    <p className="text-3xl font-heading text-gold" data-testid="consent-kpi-rate">{consentStats.totals.acceptance_rate}%</p>
                    <p className="text-xs text-white/40 mt-2">30gg: {consentStats.last_30_days.acceptance_rate}%</p>
                  </div>
                </div>

                {/* Daily chart */}
                <div className="bg-white/5 border border-white/10 rounded-lg p-6 mb-8" data-testid="consent-daily-chart">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-heading text-lg text-white">ULTIMI 30 GIORNI</h3>
                    <div className="flex gap-4 text-xs">
                      <span className="flex items-center gap-2 text-white/60"><span className="w-3 h-3 bg-green-400/70 inline-block"></span>Accettati</span>
                      <span className="flex items-center gap-2 text-white/60"><span className="w-3 h-3 bg-red-400/70 inline-block"></span>Rifiutati</span>
                    </div>
                  </div>
                  {(() => {
                    const series = consentStats.daily_series || [];
                    const maxTotal = Math.max(1, ...series.map(d => d.total));
                    return (
                      <div className="flex items-end gap-1 h-40">
                        {series.map((d) => {
                          const grantedH = (d.granted / maxTotal) * 100;
                          const deniedH = (d.denied / maxTotal) * 100;
                          const dayNum = d.date.slice(8, 10);
                          const showLabel = ['01', '10', '20'].includes(dayNum) || series.indexOf(d) === series.length - 1;
                          return (
                            <div key={d.date} className="flex-1 flex flex-col items-center group relative">
                              <div className="w-full flex flex-col-reverse h-full">
                                <div style={{ height: `${grantedH}%` }} className="bg-green-400/70 hover:bg-green-400 transition-colors" title={`${d.date}: ${d.granted} accettati`}></div>
                                <div style={{ height: `${deniedH}%` }} className="bg-red-400/70 hover:bg-red-400 transition-colors" title={`${d.date}: ${d.denied} rifiutati`}></div>
                              </div>
                              <span className={`text-[9px] text-white/40 mt-1 ${showLabel ? '' : 'invisible'}`}>{dayNum}</span>
                              <div className="absolute bottom-full mb-1 hidden group-hover:block bg-black/90 border border-white/20 rounded px-2 py-1 text-[10px] text-white whitespace-nowrap z-10">
                                {d.date}: {d.granted}✓ / {d.denied}✗
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </div>

                {/* Device breakdown */}
                <div className="bg-white/5 border border-white/10 rounded-lg p-6 mb-8" data-testid="consent-by-device">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-heading text-lg text-white">DESKTOP vs MOBILE</h3>
                    <span className="text-xs text-white/40">tasso di accettazione per dispositivo</span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {(consentStats.by_device_all_time || []).map((d) => {
                      const label = d.device === 'desktop' ? 'Desktop' : d.device === 'mobile' ? 'Mobile' : 'Tablet';
                      const d30 = (consentStats.by_device_30d || []).find(x => x.device === d.device) || { total: 0, acceptance_rate: 0 };
                      return (
                        <div key={d.device} className="bg-black/30 border border-white/10 rounded p-4" data-testid={`consent-device-${d.device}`}>
                          <div className="flex items-center justify-between mb-3">
                            <span className="text-xs text-white/50 tracking-wider uppercase">{label}</span>
                            <span className="text-xs text-white/40">{d.total} eventi</span>
                          </div>
                          <p className="text-3xl font-heading text-gold mb-1">{d.acceptance_rate}%</p>
                          <p className="text-xs text-white/40">accettazione (tutti i tempi)</p>
                          <div className="mt-3 pt-3 border-t border-white/5 flex items-center justify-between text-xs">
                            <span className="text-white/50">Ultimi 30gg:</span>
                            <span className="text-white/80 font-semibold">{d30.acceptance_rate}%<span className="text-white/40 font-normal"> ({d30.total})</span></span>
                          </div>
                          {d.total > 0 && (
                            <div className="mt-3 flex gap-1 h-2 rounded overflow-hidden bg-white/5">
                              <div style={{ width: `${d.acceptance_rate}%` }} className="bg-green-400/70"></div>
                              <div style={{ width: `${100 - d.acceptance_rate}%` }} className="bg-red-400/70"></div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Top pages + Recent events */}
                <div className="grid lg:grid-cols-2 gap-6">
                  <div className="bg-white/5 border border-white/10 rounded-lg p-6" data-testid="consent-top-pages">
                    <h3 className="font-heading text-lg text-white mb-4">TOP PAGINE PER EVENTI</h3>
                    {consentStats.top_pages.length === 0 ? (
                      <p className="text-white/40 text-sm">Nessun dato disponibile</p>
                    ) : (
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-white/40 text-xs tracking-wider border-b border-white/10">
                            <th className="text-left py-2">PAGINA</th>
                            <th className="text-right py-2">TOT</th>
                            <th className="text-right py-2">%</th>
                          </tr>
                        </thead>
                        <tbody>
                          {consentStats.top_pages.map((p) => (
                            <tr key={p.page} className="border-b border-white/5">
                              <td className="py-2.5 text-white/80 font-mono text-xs">{p.page || '—'}</td>
                              <td className="py-2.5 text-right text-white/70">{p.total}</td>
                              <td className="py-2.5 text-right text-gold font-semibold">{p.acceptance_rate}%</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>

                  <div className="bg-white/5 border border-white/10 rounded-lg p-6" data-testid="consent-recent-events">
                    <h3 className="font-heading text-lg text-white mb-4">ULTIMI 20 EVENTI</h3>
                    {consentStats.recent.length === 0 ? (
                      <p className="text-white/40 text-sm">Nessun evento registrato</p>
                    ) : (
                      <div className="space-y-2 max-h-96 overflow-y-auto">
                        {consentStats.recent.map((r, idx) => (
                          <div key={idx} className="flex items-center gap-3 text-xs border-b border-white/5 pb-2">
                            <span className={`w-2 h-2 rounded-full flex-shrink-0 ${r.choice === 'granted' ? 'bg-green-400' : 'bg-red-400'}`}></span>
                            <span className="text-white/40 font-mono text-[10px] w-32 flex-shrink-0">{r.created_at ? new Date(r.created_at).toLocaleString('it-IT') : '—'}</span>
                            <span className="text-white/70 truncate flex-1 font-mono">{r.page || '—'}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <p className="text-white/30 text-xs mt-6 text-center">
                  I dati vengono raccolti dal 14 agosto 2026, quando è stato attivato il banner cookie. Prima di quella data non è possibile calcolare il tasso di consenso.
                </p>
              </>
            )}
          </div>
        )}

        {/* REPORT 97 € TAB */}
        {adminTab === 'report97' && <ReportOrdersPanel token={token} />}
        {adminTab === 'seo' && <PrerenderStatusPanel token={token} />}
        {adminTab === 'qmdp' && (
          <div className="space-y-6">
            <QmdpGestionePanel token={token} />
            <QmdpTestPanel token={token} />
          </div>
        )}

        {/* STATISTICHE SITO TAB */}
        {adminTab === 'stats' && (
          <SiteStatsPanel
            data={siteStats}
            loading={siteStatsLoading}
            month={statsMonth}
            setMonth={setStatsMonth}
            onRefresh={(m) => fetchSiteStats(m)}
            token={token}
          />
        )}

        {/* CHECK-UP ANALYTICS TAB */}
        {adminTab === 'checkup' && (
          <div data-testid="checkup-stats-panel">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-xl text-white">FUNNEL CHECK-UP FISCALE</h2>
              <div className="flex items-center gap-2">
                <button
                  onClick={fetchCheckupStats}
                  className="flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 text-white/70 px-4 py-2 rounded text-sm transition-all"
                  data-testid="checkup-refresh-btn"
                >
                  <RefreshCw size={14} /> Aggiorna
                </button>
              </div>
            </div>

            <div className="mb-6 p-4 bg-white/[0.03] border border-white/10 rounded-lg" data-testid="scheduler-controls">
              <div className="flex items-center justify-between mb-3">
                <p className="text-white/50 text-xs tracking-wider uppercase">Automazioni email — stato & controlli</p>
                <button
                  onClick={fetchFeatureFlags}
                  className="text-[10px] text-white/50 hover:text-gold"
                >
                  ricarica stato
                </button>
              </div>

              {featureFlags && (
                <div className="space-y-2 mb-4">
                  {[
                    { k: 'checkup_reminders_enabled', label: 'Reminder abbandoni (ogni 30 min, 24-48h dopo)' },
                    { k: 'checkup_testimonials_enabled', label: 'Richieste testimonianza (15 giorni dopo il pagamento)' },
                    { k: 'checkup_weekly_digest_enabled', label: 'Riepilogo settimanale a te (lunedì mattina)' },
                  ].map((f) => {
                    const active = featureFlags[f.k] !== false;
                    return (
                      <div key={f.k} className="flex items-center justify-between bg-black/30 border border-white/10 rounded p-3">
                        <div>
                          <p className="text-white/80 text-sm">{f.label}</p>
                          <p className={`text-[10px] mt-0.5 ${active ? 'text-green-400' : 'text-red-300'}`}>
                            {active ? '● ATTIVO' : '⏸ SOSPESO'}
                          </p>
                        </div>
                        <button
                          onClick={() => toggleFlag(f.k, !active)}
                          className={`text-xs px-3 py-1.5 rounded transition-all ${active
                            ? 'bg-red-500/20 hover:bg-red-500/30 text-red-200 border border-red-500/30'
                            : 'bg-green-500/20 hover:bg-green-500/30 text-green-200 border border-green-500/30'}`}
                          data-testid={`flag-toggle-${f.k}`}
                        >
                          {active ? 'Sospendi' : 'Riattiva'}
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
              {!featureFlags && (
                <button
                  onClick={fetchFeatureFlags}
                  className="text-xs bg-white/5 hover:bg-white/10 border border-white/10 text-white/70 px-3 py-1.5 rounded mb-3"
                >
                  Carica stato automazioni
                </button>
              )}

              <div className="flex flex-wrap gap-2 pt-3 border-t border-white/10">
                <button
                  onClick={async () => {
                    try {
                      await axios.post(`${API}/admin/scheduler/run/reminders`, {}, { headers: { Authorization: `Bearer ${token}` } });
                      toast.success('Reminder job eseguito');
                    } catch { toast.error('Errore'); }
                  }}
                  className="text-xs bg-white/5 hover:bg-white/10 border border-white/10 text-white/70 px-3 py-1.5 rounded"
                  data-testid="run-reminders-btn"
                >
                  Esegui ora Reminder
                </button>
                <button
                  onClick={async () => {
                    try {
                      await axios.post(`${API}/admin/scheduler/run/weekly-digest`, {}, { headers: { Authorization: `Bearer ${token}` } });
                      toast.success('Riepilogo settimanale inviato');
                    } catch { toast.error('Errore'); }
                  }}
                  className="text-xs bg-white/5 hover:bg-white/10 border border-white/10 text-white/70 px-3 py-1.5 rounded"
                  data-testid="run-digest-btn"
                >
                  Invia ora Riepilogo
                </button>
                <button
                  onClick={async () => {
                    try {
                      await axios.post(`${API}/admin/scheduler/run/testimonials`, {}, { headers: { Authorization: `Bearer ${token}` } });
                      toast.success('Testimonial job eseguito');
                    } catch { toast.error('Errore'); }
                  }}
                  className="text-xs bg-white/5 hover:bg-white/10 border border-white/10 text-white/70 px-3 py-1.5 rounded"
                  data-testid="run-testimonials-btn"
                >
                  Esegui ora Testimonianze
                </button>
                <span className="text-white/30 text-[10px] self-center ml-2">(rispettano il flag: se sospeso, non partono)</span>
              </div>

              <div className="mt-4 pt-3 border-t border-white/10">
                <button
                  onClick={downloadLeadsCsv}
                  className="text-xs bg-gold/20 hover:bg-gold/30 border border-gold/40 text-gold px-4 py-2 rounded flex items-center gap-2"
                  data-testid="download-csv-btn"
                >
                  <Download size={14} /> Esporta lead check-up (CSV)
                </button>
                <p className="text-white/30 text-[10px] mt-2">Tutti i contatti raccolti dal check-up con status, tipo soggetto, data, dettagli recap. Pronto per import CRM.</p>
              </div>
            </div>

            {checkupLoading && !checkupStats && (
              <div className="text-center py-16 text-white/50">Caricamento...</div>
            )}

            {checkupStats && (
              <>
                {/* Funnel */}
                <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-8" data-testid="checkup-funnel">
                  {[
                    { key: 'started', label: 'AVVIATI', value: checkupStats.totals.started, color: 'text-white' },
                    { key: 'completed', label: 'COMPLETATI', value: checkupStats.totals.completed, color: 'text-gold' },
                    { key: 'cta_click', label: 'CTA CLICCATA', value: checkupStats.totals.cta_click, color: 'text-blue-300' },
                    { key: 'recap_sent', label: 'EMAIL INVIATE', value: checkupStats.totals.recap_sent, color: 'text-purple-300' },
                    { key: 'paid', label: 'PAGATI', value: checkupStats.totals.paid, color: 'text-green-400' },
                  ].map((s) => (
                    <div key={s.key} className="bg-white/5 border border-white/10 rounded-lg p-5" data-testid={`checkup-kpi-${s.key}`}>
                      <p className="text-xs text-white/50 tracking-wider mb-2">{s.label}</p>
                      <p className={`text-3xl font-heading ${s.color}`}>{s.value}</p>
                      {checkupStats.last_30_days[s.key] !== undefined && (
                        <p className="text-xs text-white/40 mt-2">30gg: {checkupStats.last_30_days[s.key]}</p>
                      )}
                    </div>
                  ))}
                </div>

                {/* Conversion rates */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
                  <div className="bg-gradient-to-br from-gold/10 to-transparent border border-gold/30 rounded-lg p-5">
                    <p className="text-xs text-white/50 tracking-wider mb-2">TASSO COMPLETAMENTO</p>
                    <p className="text-2xl font-heading text-gold" data-testid="checkup-rate-completion">{checkupStats.conversion.completion_rate}%</p>
                    <p className="text-[10px] text-white/40 mt-2">Completati / Avviati</p>
                  </div>
                  <div className="bg-white/5 border border-blue-400/20 rounded-lg p-5">
                    <p className="text-xs text-white/50 tracking-wider mb-2">CLICK SULL'OFFERTA</p>
                    <p className="text-2xl font-heading text-blue-300" data-testid="checkup-rate-cta">{checkupStats.conversion.cta_rate}%</p>
                    <p className="text-[10px] text-white/40 mt-2">CTA / Completati</p>
                  </div>
                  <div className="bg-white/5 border border-green-400/20 rounded-lg p-5">
                    <p className="text-xs text-white/50 tracking-wider mb-2">CONVERSIONE PAGAMENTO</p>
                    <p className="text-2xl font-heading text-green-400" data-testid="checkup-rate-paid">{checkupStats.conversion.paid_rate}%</p>
                    <p className="text-[10px] text-white/40 mt-2">Pagati / Click CTA</p>
                  </div>
                  <div className="bg-white/5 border border-white/10 rounded-lg p-5">
                    <p className="text-xs text-white/50 tracking-wider mb-2">CONVERSIONE TOTALE</p>
                    <p className="text-2xl font-heading text-white" data-testid="checkup-rate-overall">{checkupStats.conversion.overall_rate}%</p>
                    <p className="text-[10px] text-white/40 mt-2">Pagati / Avviati</p>
                  </div>
                </div>

                {/* Daily chart */}
                <div className="bg-white/5 border border-white/10 rounded-lg p-6 mb-8" data-testid="checkup-daily-chart">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-heading text-lg text-white">ULTIMI 30 GIORNI</h3>
                    <div className="flex gap-4 text-xs">
                      <span className="flex items-center gap-2 text-white/60"><span className="w-3 h-3 bg-white/50 inline-block"></span>Avviati</span>
                      <span className="flex items-center gap-2 text-white/60"><span className="w-3 h-3 bg-gold inline-block"></span>Completati</span>
                      <span className="flex items-center gap-2 text-white/60"><span className="w-3 h-3 bg-blue-300 inline-block"></span>CTA</span>
                    </div>
                  </div>
                  {(() => {
                    const series = checkupStats.daily_series || [];
                    const maxVal = Math.max(1, ...series.map(d => Math.max(d.started, d.completed, d.cta_click)));
                    return (
                      <div className="flex items-end gap-1 h-40">
                        {series.map((d) => {
                          const dayNum = d.date.slice(8, 10);
                          const showLabel = ['01', '10', '20'].includes(dayNum) || series.indexOf(d) === series.length - 1;
                          return (
                            <div key={d.date} className="flex-1 flex items-end gap-[1px] group relative">
                              <div style={{ height: `${(d.started / maxVal) * 100}%` }} className="flex-1 bg-white/50 min-h-[1px]" title={`Avviati: ${d.started}`}></div>
                              <div style={{ height: `${(d.completed / maxVal) * 100}%` }} className="flex-1 bg-gold min-h-[1px]" title={`Completati: ${d.completed}`}></div>
                              <div style={{ height: `${(d.cta_click / maxVal) * 100}%` }} className="flex-1 bg-blue-300 min-h-[1px]" title={`CTA: ${d.cta_click}`}></div>
                              <div className="absolute bottom-full mb-1 hidden group-hover:block bg-black/90 border border-white/20 rounded px-2 py-1 text-[10px] text-white whitespace-nowrap z-10">
                                {d.date}: {d.started}▪ {d.completed}▪ {d.cta_click}▪
                              </div>
                              <span className={`text-[9px] text-white/40 absolute -bottom-4 left-1/2 -translate-x-1/2 ${showLabel ? '' : 'invisible'}`}>{dayNum}</span>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </div>

                {/* Imbuto per domanda */}
                <div className="bg-white/5 border border-white/10 rounded-lg p-6 mb-8" data-testid="checkup-funnel-domande">
                  <h3 className="font-heading text-lg text-white mb-1">DOVE SI FERMANO (ULTIMI 30 GIORNI)</h3>
                  <p className="text-white/40 text-xs mb-5">Sessioni distinte arrivate a ogni domanda. Nessun dato personale: solo percorso, numero della domanda e giorno.</p>
                  {(() => {
                    const f = checkupStats.funnel_domande || {};
                    const gruppi = [
                      { key: 'comune', titolo: 'DOMANDE COMUNI' },
                      { key: 'societa', titolo: 'PERCORSO SOCIETÀ' },
                      { key: 'persona', titolo: 'PERCORSO PERSONA FISICA / PARTITA IVA' },
                    ];
                    const vuoto = gruppi.every(g => !(f[g.key] || []).length);
                    if (vuoto) {
                      return <p className="text-white/50 text-sm">Nessun dato ancora: l'imbuto si popola dalle prossime visite.</p>;
                    }
                    return (
                      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        {gruppi.map(g => {
                          const righe = f[g.key] || [];
                          const max = Math.max(1, ...righe.map(r => r.sessioni));
                          return (
                            <div key={g.key} data-testid={`checkup-funnel-${g.key}`}>
                              <p className="text-xs text-gold tracking-wider uppercase mb-3">{g.titolo}</p>
                              {righe.length === 0 && <p className="text-white/40 text-xs">Nessun dato</p>}
                              {righe.map(r => (
                                <div key={r.campo} className="mb-2">
                                  <div className="flex items-baseline justify-between gap-3">
                                    <span className={`text-xs ${r.campo === 'contact' ? 'text-gold font-bold' : 'text-white/70'}`}>
                                      {r.numero !== 99 ? `${r.numero}. ` : ''}{r.testo}
                                    </span>
                                    <span className="text-xs text-white tabular-nums">{r.sessioni}</span>
                                  </div>
                                  <div className="h-1.5 bg-white/10 rounded mt-1">
                                    <div
                                      className={`h-1.5 rounded ${r.campo === 'contact' ? 'bg-gold' : 'bg-white/50'}`}
                                      style={{ width: `${(r.sessioni / max) * 100}%` }}
                                    ></div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </div>

                {/* Device breakdown for completed */}
                <div className="bg-white/5 border border-white/10 rounded-lg p-6" data-testid="checkup-by-device">
                  <h3 className="font-heading text-lg text-white mb-4">CHECK-UP COMPLETATI PER DISPOSITIVO</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {(checkupStats.by_device || []).map((d) => {
                      const label = d.device === 'desktop' ? 'Desktop' : d.device === 'mobile' ? 'Mobile' : 'Tablet';
                      const totalDev = (checkupStats.by_device || []).reduce((s, x) => s + x.completed, 0);
                      const pct = totalDev ? Math.round((d.completed / totalDev) * 100) : 0;
                      return (
                        <div key={d.device} className="bg-black/30 border border-white/10 rounded p-4">
                          <p className="text-xs text-white/50 tracking-wider uppercase mb-2">{label}</p>
                          <p className="text-3xl font-heading text-white">{d.completed}</p>
                          <p className="text-xs text-white/40 mt-1">{pct}% del totale</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* ABBANDONI CHECK-UP TAB */}
        {adminTab === 'abandoned' && (
          <div data-testid="abandoned-panel">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="font-heading text-xl text-white">ABBANDONI CHECK-UP</h2>
                <p className="text-white/50 text-xs mt-1">Utenti che hanno inserito i contatti ma non hanno completato il check-up (attesa &gt; 10 minuti)</p>
              </div>
              <button
                onClick={fetchAbandonedLeads}
                className="flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 text-white/70 px-4 py-2 rounded text-sm transition-all"
                data-testid="abandoned-refresh-btn"
              >
                <RefreshCw size={14} /> Aggiorna
              </button>
            </div>

            {abandonedLoading && !abandonedLeads && (
              <div className="text-center py-16 text-white/50">Caricamento...</div>
            )}

            {abandonedLeads && (
              <>
                <div className="mb-6 bg-white/5 border border-white/10 rounded-lg p-5 inline-block">
                  <p className="text-xs text-white/50 tracking-wider mb-2">ABBANDONI CON CONTATTO</p>
                  <p className="text-3xl font-heading text-gold" data-testid="abandoned-count">{abandonedLeads.count}</p>
                </div>

                <div className="bg-white/5 border border-white/10 rounded-lg overflow-hidden">
                  {abandonedLeads.items.length === 0 ? (
                    <div className="text-center py-16">
                      <p className="text-white/50">Nessun abbandono con contatto negli ultimi giorni. 🎉</p>
                    </div>
                  ) : (
                    <table className="w-full text-sm" data-testid="abandoned-table">
                      <thead className="border-b border-white/10 bg-white/[0.02]">
                        <tr>
                          <th className="text-left text-white/50 text-xs tracking-wider p-4">DATA</th>
                          <th className="text-left text-white/50 text-xs tracking-wider p-4">NOME · AZIENDA</th>
                          <th className="text-left text-white/50 text-xs tracking-wider p-4">CONTATTI</th>
                          <th className="text-left text-white/50 text-xs tracking-wider p-4">SOGGETTO</th>
                          <th className="text-left text-white/50 text-xs tracking-wider p-4">DEVICE</th>
                          <th className="text-right text-white/50 text-xs tracking-wider p-4">AZIONI</th>
                        </tr>
                      </thead>
                      <tbody>
                        {abandonedLeads.items.map((lead) => {
                          const subj = encodeURIComponent(`Ho notato che hai iniziato il check-up fiscale su casalisilvia.com`);
                          const body = encodeURIComponent(`Ciao ${lead.nome || ''},\n\nho visto che hai iniziato il check-up online ma non lo hai completato. Ti aiuto io a chiuderlo? Se hai una domanda specifica scrivimela pure — rispondo io.\n\nSilvia`);
                          const waTxt = encodeURIComponent(`Ciao ${lead.nome || ''}, ho notato che hai iniziato il check-up fiscale sul mio sito. Posso aiutarti a completarlo o rispondere a una domanda?`);
                          const telClean = (lead.tel || '').replace(/\D/g, '');
                          return (
                            <tr key={lead.id} className="border-b border-white/5 hover:bg-white/5" data-testid={`abandoned-row-${lead.id}`}>
                              <td className="p-4 text-white/60 text-xs font-mono whitespace-nowrap">
                                {lead.created_at ? new Date(lead.created_at).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—'}
                              </td>
                              <td className="p-4">
                                <p className="text-white text-sm font-semibold">{lead.nome || '—'}</p>
                                <p className="text-white/50 text-xs">{lead.azienda || '—'}</p>
                              </td>
                              <td className="p-4">
                                <p className="text-gold text-xs font-mono">{lead.email}</p>
                                <p className="text-white/50 text-xs">{lead.tel || '—'}</p>
                              </td>
                              <td className="p-4 text-white/70 text-xs">
                                {lead.tipo || '—'}{lead.regime ? ` · ${lead.regime}` : ''}{lead.regione ? ` · ${lead.regione}` : ''}
                              </td>
                              <td className="p-4 text-white/50 text-xs uppercase">{lead.device || '—'}</td>
                              <td className="p-4 text-right whitespace-nowrap">
                                <a
                                  href={`mailto:${lead.email}?subject=${subj}&body=${body}`}
                                  className="inline-flex items-center gap-1 text-gold hover:text-yellow-300 text-xs mr-3"
                                  data-testid={`abandoned-email-${lead.id}`}
                                >
                                  <Mail size={13} /> Email
                                </a>
                                {telClean && (
                                  <a
                                    href={`https://wa.me/${telClean.startsWith('39') ? telClean : '39' + telClean}?text=${waTxt}`}
                                    target="_blank" rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1 text-green-400 hover:text-green-300 text-xs"
                                    data-testid={`abandoned-whatsapp-${lead.id}`}
                                  >
                                    <MessageCircle size={13} /> WhatsApp
                                  </a>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
                <p className="text-white/30 text-xs mt-6">
                  I lead vengono raccolti quando l'utente arriva alla schermata contatti del check-up. Se completano il check-up entro pochi secondi, non compaiono qui.
                </p>
              </>
            )}
          </div>
        )}

        {/* FORFETTARI TAB */}
        {adminTab === 'forfettari' && (
          <div data-testid="forfettari-panel">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="font-heading text-xl text-white">CLIENTI FORFETTARI</h2>
                <p className="text-white/50 text-xs mt-1">Abbonamenti attivi, IBAN per gli F24, portale di fatturazione e conguagli di dicembre</p>
              </div>
              <button
                onClick={fetchForfettari}
                className="flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 text-white/70 px-4 py-2 rounded text-sm transition-all"
                data-testid="forfettari-refresh-btn"
              >
                <RefreshCw size={14} /> Aggiorna
              </button>
            </div>

            {forfLoading && !forfettari && (
              <div className="text-center py-16 text-white/50">Caricamento...</div>
            )}

            {forfettari && (
              <>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                  <div className="bg-white/5 border border-white/10 rounded-lg p-5">
                    <p className="text-xs text-white/50 tracking-wider mb-2">CLIENTI</p>
                    <p className="text-3xl font-heading text-gold" data-testid="forf-stat-clienti">{forfettari.stats.clienti}</p>
                  </div>
                  <div className="bg-white/5 border border-white/10 rounded-lg p-5">
                    <p className="text-xs text-white/50 tracking-wider mb-2">CON PORTALE ATTIVO</p>
                    <p className="text-3xl font-heading text-gold" data-testid="forf-stat-portale">{forfettari.stats.con_portale}</p>
                  </div>
                  <div className="bg-white/5 border border-white/10 rounded-lg p-5">
                    <p className="text-xs text-white/50 tracking-wider mb-2">CONGUAGLI IN ATTESA (DIC)</p>
                    <p className="text-3xl font-heading text-gold" data-testid="forf-stat-conguagli">
                      {forfettari.stats.conguagli_attesa_n}
                      <span className="text-sm text-white/50 ml-2">· {forfettari.stats.conguagli_attesa_tot.toFixed(0)} €</span>
                    </p>
                  </div>
                  <div className="bg-white/5 border border-white/10 rounded-lg p-5">
                    <p className="text-xs text-white/50 tracking-wider mb-2">REGISTRO {forfettari.stats.anno}</p>
                    <p className="text-3xl font-heading text-gold" data-testid="forf-stat-registro">
                      {forfettari.stats.registro_anno_n}
                      <span className="text-sm text-white/50 ml-2">· {forfettari.stats.registro_anno_tot.toFixed(0)} €</span>
                    </p>
                  </div>
                </div>

                <div className="bg-white/5 border border-white/10 rounded-lg overflow-hidden overflow-x-auto">
                  {forfettari.items.length === 0 ? (
                    <div className="text-center py-16">
                      <p className="text-white/50">Nessun cliente forfettari ancora. Appariranno qui dopo la prima attivazione.</p>
                    </div>
                  ) : (
                    <table className="w-full text-sm min-w-[900px]" data-testid="forfettari-table">
                      <thead className="border-b border-white/10 bg-white/[0.02]">
                        <tr>
                          <th className="text-left text-white/50 text-xs tracking-wider p-4">CLIENTE</th>
                          <th className="text-left text-white/50 text-xs tracking-wider p-4">PIANO</th>
                          <th className="text-left text-white/50 text-xs tracking-wider p-4">ATTIVATO</th>
                          <th className="text-left text-white/50 text-xs tracking-wider p-4">IBAN F24</th>
                          <th className="text-left text-white/50 text-xs tracking-wider p-4">PREVIDENZA</th>
                          <th className="text-left text-white/50 text-xs tracking-wider p-4">PORTALE</th>
                          <th className="text-left text-white/50 text-xs tracking-wider p-4">CONGUAGLIO</th>
                          <th className="text-right text-white/50 text-xs tracking-wider p-4">REGISTRO</th>
                        </tr>
                      </thead>
                      <tbody>
                        {forfettari.items.map((c, i) => (
                          <tr key={c.email} className="border-b border-white/5 hover:bg-white/5" data-testid={`forfettari-row-${i}`}>
                            <td className="p-4">
                              <p className="text-white text-sm font-semibold">{c.nome || '—'}</p>
                              <p className="text-gold text-xs font-mono">{c.email}</p>
                              {c.telefono && <p className="text-white/40 text-xs">{c.telefono}</p>}
                            </td>
                            <td className="p-4 text-white/70 text-xs">{c.piano || <span className="text-white/30">solo questionario</span>}</td>
                            <td className="p-4 text-white/60 text-xs font-mono whitespace-nowrap">
                              {c.attivato_il ? new Date(c.attivato_il).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: '2-digit' }) : '—'}
                            </td>
                            <td className="p-4">
                              {c.iban ? (
                                <button
                                  onClick={() => { navigator.clipboard.writeText(c.iban); toast.success('IBAN copiato'); }}
                                  className="text-white/80 text-xs font-mono hover:text-gold transition-colors"
                                  title="Clicca per copiare"
                                  data-testid={`forfettari-iban-${i}`}
                                >
                                  {c.iban}
                                </button>
                              ) : <span className="text-white/30 text-xs">non ancora fornito</span>}
                            </td>
                            <td className="p-4 text-white/60 text-xs">{c.gestione || '—'}</td>
                            <td className="p-4">
                              {c.portale ? (
                                c.portale.attivo ? (
                                  <div>
                                    <span className="inline-block bg-gold/20 text-gold text-[10px] font-bold px-2 py-1 rounded">ATTIVO · {c.portale.rinnovo_anno}</span>
                                    {c.portale.addebito_fallito && <p className="text-red-400 text-[10px] mt-1">ultimo addebito fallito</p>}
                                    {c.portale.preavviso_anno && <p className="text-white/40 text-[10px] mt-1">preavviso {c.portale.preavviso_anno} inviato</p>}
                                  </div>
                                ) : (
                                  <span className="inline-block bg-white/10 text-white/50 text-[10px] font-bold px-2 py-1 rounded">DISATTIVATO</span>
                                )
                              ) : <span className="text-white/30 text-xs">proprio portale</span>}
                            </td>
                            <td className="p-4">
                              {c.conguaglio ? (
                                <div className="text-xs">
                                  <span className="text-white/80 font-mono">{Number(c.conguaglio.importo).toFixed(0)} €</span>
                                  <span className={`ml-2 text-[10px] font-bold px-2 py-0.5 rounded ${
                                    c.conguaglio.stato === 'in_attesa' ? 'bg-yellow-500/20 text-yellow-300' :
                                    c.conguaglio.stato === 'addebitato' ? 'bg-green-500/20 text-green-400' :
                                    c.conguaglio.stato === 'fallito' ? 'bg-red-500/20 text-red-400' : 'bg-white/10 text-white/50'
                                  }`}>{(c.conguaglio.stato || '').replace('_', ' ').toUpperCase()}</span>
                                  <p className="text-white/30 text-[10px] mt-0.5">dicembre {c.conguaglio.anno}</p>
                                </div>
                              ) : <span className="text-white/30 text-xs">—</span>}
                            </td>
                            <td className="p-4 text-right text-xs whitespace-nowrap">
                              <span className="text-white/80">{c.registro_n}</span>
                              <span className="text-white/40"> · {c.registro_tot.toFixed(0)} €</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
                <p className="text-white/30 text-xs mt-6">
                  I clienti appaiono dopo il pagamento dell'abbonamento o la compilazione del questionario. "Registro" = fatture registrate automaticamente (abbonamenti, portale, conguagli). Per cambiare l'IBAN di un cliente serve una sua email allo studio.
                </p>
              </>
            )}
          </div>
        )}
      </div>

      {/* Resend Email Modal */}
      {resendModal && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="resend-modal" onClick={closeResendModal}>
          <div className="bg-[#0a0a0a] border border-white/10 rounded-xl max-w-lg w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-heading text-xl text-white mb-2">Reinvia email a</h3>
            <p className="text-gold text-sm font-mono mb-1">{resendModal.customer_email}</p>
            <p className="text-white/50 text-xs mb-6">
              Prodotto: {resendModal.product_title}
              {resendModal.product_current_version && (
                <span className="ml-2 text-[10px] font-mono text-gold/70 bg-gold/10 px-1.5 py-0.5 rounded">{resendModal.product_current_version}</span>
              )}
            </p>
            <p className="text-white/70 text-sm mb-4">Scegli il motivo del rinvio:</p>
            <div className="space-y-3">
              <button
                onClick={() => confirmResend('resend')}
                className="w-full text-left bg-white/5 hover:bg-white/10 border border-white/10 hover:border-gold/30 rounded-lg p-4 transition-all"
                data-testid="resend-mode-standard"
              >
                <p className="text-white font-semibold text-sm mb-1">Rinvio standard <span className="text-white/40 text-xs font-normal">(malfunzionamento / email persa)</span></p>
                <p className="text-white/50 text-xs">Invia la stessa email che ha ricevuto quando ha acquistato. Nessun testo aggiuntivo.</p>
              </button>
              <button
                onClick={() => confirmResend('update')}
                className="w-full text-left bg-gold/5 hover:bg-gold/10 border border-gold/30 hover:border-gold/50 rounded-lg p-4 transition-all"
                data-testid="resend-mode-update"
              >
                <p className="text-white font-semibold text-sm mb-1">
                  Rilascio nuova versione
                  {resendModal.product_current_version && (
                    <span className="ml-2 text-[10px] font-mono text-gold bg-gold/20 px-1.5 py-0.5 rounded">{resendModal.product_current_version}</span>
                  )}
                </p>
                <p className="text-white/50 text-xs">
                  L'email comunica che è disponibile una nuova versione del file
                  {resendModal.product_current_version && <> ({resendModal.product_current_version})</>}
                  e lo invita a scaricarla di nuovo.
                </p>
              </button>
            </div>
            <button onClick={closeResendModal} className="mt-6 w-full text-white/50 hover:text-white text-xs tracking-wider py-2" data-testid="resend-cancel">
              Annulla
            </button>
          </div>
        </div>
      )}
    </main>
  );
};

// Portale Documenti Page

export { AdminPage };
